/**
 * regulatoryLisibilite — une mention réglementaire est-elle LISIBLE là où elle
 * vient d'être posée ?
 *
 * Pourquoi ce module existe. Un modèle .lfgr enregistré sur une étiquette dont
 * le panneau de droite est un aplat NOIR y porte ses mentions en BLANC. Appliqué
 * à une étiquette sans cet aplat, le modèle repose fidèlement le placement et la
 * couleur : du blanc sur du blanc. Les mentions sont là, exactes, complètes — et
 * personne ne les voit. L'utilisateur en conclut, très logiquement, que le modèle
 * ne les a pas dupliquées.
 * *C'est le retour utilisateur du 2026-08-28 : trois mentions signalées
 * « absentes » étaient posées au bon endroit, en #ffffff sur fond blanc.*
 *
 * Le principe de ce contrôle : on ne devine pas, on ne corrige pas, on DIT.
 * Recolorer d'autorité une mention légale serait pire — le blanc est peut-être
 * voulu, et l'aplat arrivera peut-être après. Mais le silence n'est pas une
 * option : un PDF sans mentions lisibles annoncé comme réussi, c'est le défaut
 * le plus coûteux de toute la chaîne.
 *
 * Module PUR (aucun import de `components/`, aucune dépendance au store) : il se
 * rejoue en Node, cf. `banc/banc-lisibilite.mjs`.
 */
import { styleEffectifTexte } from './richTextModel';

/** Contraste en dessous duquel deux couleurs ne se distinguent pas à l'œil.
 *  Le seuil WCAG de lisibilité d'un texte est 4,5:1 ; on ne cherche pas à
 *  arbitrer le confort de lecture, seulement à attraper l'invisible. 1,5:1
 *  laisse donc passer tout ce qui se voit, même mal. */
export const CONTRASTE_MINIMUM = 1.5;

/** Contraste en dessous duquel une mention, lisible, reste PEU contrastée : le seuil
 *  WCAG d'un petit texte (4,5:1). Le CLP révisé par l'Omnibus VI (anticipé le
 *  24/10/2026) ne demande plus du noir sur fond blanc, mais des « couleurs contrastées
 *  par rapport à l'arrière-plan » (annexe I, 1.2.1.5.2 a) : une mention foncée sur un
 *  cartouche clair coloré y satisfait ; un gris moyen sur un crème, non. Le noir sur un
 *  cartouche clair reste le défaut de composition, le plus sûr. Sert à `controler`
 *  (CONTRASTE_FAIBLE, à vérifier) ; l'alerte de l'éditeur garde CONTRASTE_MINIMUM. */
export const CONTRASTE_SUFFISANT = 4.5;

/** Part du calque qu'un élément doit couvrir pour compter dans son fond. */
const COUVERTURE_SUFFISANTE = 0.5;

/** #abc / #aabbcc / rgb(…) → { r, g, b } sur 0-255, ou null si illisible. */
export function versRgb(couleur) {
    if (typeof couleur !== 'string') return null;
    const c = couleur.trim();
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
    if (hex) {
        const h = hex[1];
        const p = h.length === 3
            ? [h[0] + h[0], h[1] + h[1], h[2] + h[2]]
            : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
        return { r: parseInt(p[0], 16), g: parseInt(p[1], 16), b: parseInt(p[2], 16) };
    }
    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(c);
    if (rgb) {
        return { r: Math.round(+rgb[1]), g: Math.round(+rgb[2]), b: Math.round(+rgb[3]) };
    }
    return null;
}

/** Luminance relative WCAG d'une couleur sRGB. */
function luminance({ r, g, b }) {
    const canal = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Rapport de contraste WCAG entre deux luminances. */
const rapport = (la, lb) => (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);

/** Rapport de contraste WCAG entre deux couleurs (1 = identiques, 21 = max). */
export function contraste(a, b) {
    const ca = versRgb(a);
    const cb = versRgb(b);
    if (!ca || !cb) return null;
    const la = luminance(ca);
    const lb = luminance(cb);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Part de la boîte `cible` couverte par la boîte `autre` (0 à 1). */
function recouvrement(cible, autre) {
    const w = Math.min(cible.x + cible.width, autre.x + autre.width) - Math.max(cible.x, autre.x);
    const h = Math.min(cible.y + cible.height, autre.y + autre.height) - Math.max(cible.y, autre.y);
    if (w <= 0 || h <= 0) return 0;
    const aire = (cible.width || 0) * (cible.height || 0);
    return aire > 0 ? (w * h) / aire : 0;
}

/** Boîte d'un élément, ou null si elle n'est pas exploitable. */
function boite(el) {
    const { x, y, width, height } = el || {};
    if (![x, y, width, height].every((v) => Number.isFinite(v))) return null;
    return { x, y, width: Math.abs(width), height: Math.abs(height) };
}

const NOIR = { r: 0, g: 0, b: 0 };
const BLANC = { r: 255, g: 255, b: 255 };
const INCONNU = { lo: NOIR, hi: BLANC };
const hex = ({ r, g, b }) => `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/**
 * Ce qu'un élément peint, réduit à une FOURCHETTE de couleurs { lo, hi } (canal par
 * canal, sur 0-255) : une couleur exacte quand on la connaît (lo = hi), toute la
 * fourchette du noir au blanc quand on ne la connaît pas. `null` : l'élément ne peint
 * pas de fond (un texte, un trait, un cadre creux).
 */
function peintureDe(el) {
    // Ne font pas un fond : un trait non rempli (le tracé de découpe, dont la boîte couvre
    // toute l'étiquette, ou un symbole au trait) et un TEXTE (il ne peint jamais de fond ;
    // le voile de TextLayer n'est qu'une aide d'édition). Mesuré le 2026-10-13 sur les 7
    // gabarits de l'atelier : la découpe, rangée en bas de la pile, et le cadre de leur
    // texte de mentions faisaient taire, CHACUN à lui seul, l'alerte de toute mention posée dessus.
    if (el.isCutContour || el.type === 'text' || el.type === 'group') return null;
    if (el.type === 'shape') {
        if (el.svgFill === false || el.shapeType === 'line') return null;
        // Un pictogramme officiel porte ses propres couleurs : on ne les lit pas.
        if (el.shapeType === 'svg' && el.svgContent && !el.svgPath) return INCONNU;
        if (el.fillType === 'gradient' && Array.isArray(el.gradient?.stops) && el.gradient.stops.length) {
            const arrets = el.gradient.stops.map((a) => versRgb(a?.color));
            if (arrets.some((a) => !a)) return INCONNU;
            const borne = (f) => ({ r: f(...arrets.map((a) => a.r)), g: f(...arrets.map((a) => a.g)), b: f(...arrets.map((a) => a.b)) });
            return { lo: borne(Math.min), hi: borne(Math.max) };
        }
        // La couleur d'une forme pleine sans couleur, au rendu (ShapeLayer).
        const c = versRgb(el.color || '#888888');
        return c ? { lo: c, hi: c } : INCONNU;
    }
    if (el.type === 'image') {
        // Un cartouche ou une pastille de la bibliothèque (kits de gamme) : la couleur de
        // son centre, là où va le texte, est connue (`centre`, recopié de la bibliothèque à
        // la pose). Un cadre CREUX laisse voir ce qu'il y a dessous.
        if (el.centre?.transparent === true) return null;
        const c = versRgb(el.centre?.couleur);
        return c ? { lo: c, hi: c } : INCONNU;
    }
    // Code-barres et le reste : la couleur sous le texte ne se lit pas.
    return INCONNU;
}

/** `dessus` (opacité 0-1) peint sur `dessous` : la fusion « normal » du navigateur, canal par canal. */
const fondre = (dessous, dessus, a) => ({
    r: a * dessus.r + (1 - a) * dessous.r, g: a * dessus.g + (1 - a) * dessous.g, b: a * dessus.b + (1 - a) * dessous.b,
});

/**
 * Le FOND réel d'un calque : le plan de travail, puis chaque élément peint DERRIÈRE lui
 * qui couvre au moins la moitié de son cadre (un cartouche, un aplat, une pastille, une
 * image), fondu selon son opacité. Rend une fourchette { lo, hi, sur } : `sur` est
 * l'élément le plus haut qui y a contribué (null : le plan de travail seul).
 * Un plan de travail dont la couleur ne se lit pas compte comme inconnu.
 */
export function fondSous(calque, canvas, elements) {
    const pile = Array.isArray(elements) ? elements : [];
    const plan = versRgb(canvas?.color_bg || '#FFFFFF');
    let lo = plan || NOIR, hi = plan || BLANC, sur = null;
    const cadre = boite(calque);
    if (!cadre) return { lo, hi, sur };
    const i = pile.findIndex((e) => e?.id === calque.id);
    for (const autre of pile.slice(0, i >= 0 ? i : pile.length)) {
        if (!autre || autre.regBlock || autre.hidden) continue;
        const b = boite(autre);
        if (!b || recouvrement(cadre, b) < COUVERTURE_SUFFISANTE) continue;
        const p = peintureDe(autre);
        const a = Math.min(Math.max(Number(autre.opacity ?? 100) / 100, 0), 1);
        if (!p || !(a > 0)) continue;
        lo = fondre(lo, p.lo, a);
        hi = fondre(hi, p.hi, a);
        sur = autre;
    }
    return { lo, hi, sur };
}

/**
 * Mentions réglementaires que rien ne distingue de leur fond.
 *
 * Le fond est celui que le calque a réellement DERRIÈRE lui (`fondSous`) : le plan de
 * travail (`canvas.color_bg`) et, par-dessus, fondus selon leur opacité, les éléments
 * qui couvrent au moins la moitié du calque — un cartouche, un aplat, une pastille.
 * *Jusqu'au 2026-10-21, un tel élément, s'il était opaque, faisait TAIRE la règle ; or
 * l'agent pose toutes ses mentions sur des cartouches (#344, #345) : le blanc sur
 * blanc du 28 août repassait sans un mot sur un cartouche blanc (relecture de Jabir,
 * 19/10). Un cartouche est le fond contre lequel on juge, pas une raison de se taire.*
 *
 * Ce qu'on ne lit pas (une image sans couleur de centre connue, un pictogramme
 * multicolore, un code-barres) élargit le fond à toute la fourchette du noir au
 * blanc. On ne signale qu'à coup sûr : quand MÊME la couleur la plus favorable de la
 * fourchette laisse la mention sous le seuil. Mieux vaut se taire que crier au loup
 * sur une mention parfaitement lisible.
 *
 * L'ordre du tableau `elements` est l'ordre d'empilement : un élément situé
 * AVANT le calque est derrière lui.
 *
 * **Cette fonction NE LÈVE JAMAIS.** C'est un diagnostic : il vaut mieux perdre
 * un avertissement qu'interrompre l'opération qu'il commente.
 *
 * @param {object[]} calques  - calques réglementaires à contrôler
 * @param {object}   canvas   - { color_bg }
 * @param {object[]} elements - toute la pile, dans l'ordre d'empilement
 * @param {{seuil?: number}} [options] - le contraste visé (CONTRASTE_MINIMUM par défaut :
 *   l'invisible ; CONTRASTE_SUFFISANT : le peu contrasté)
 * @returns {{role:string, key:string, couleur:string, contraste:number, fond:string, sur:(string|null), sur_nom?:string, name?:string}[]}
 *   `contraste` : le meilleur que permette le fond ; `fond` : sa couleur (« #A–#B » si
 *   elle n'est connue qu'à une fourchette près) ; `sur` : l'élément qui le peint.
 */
export function mentionsIllisibles(calques, canvas, elements, { seuil = CONTRASTE_MINIMUM } = {}) {
    const out = [];

    for (const calque of (calques || [])) {
      // Un calque malformé ne doit pas emporter le rapport entier, et surtout pas
      // l'opération qu'il commente : `styleEffectifTexte` traverse le rich text et
      // lève sur un `content` non itérable — un `{ content: 42 }` suffit. Comme
      // cette fonction est appelée APRÈS que le modèle a muté le store, une
      // exception ici laisserait l'étiquette modifiée et l'utilisateur sans le
      // moindre compte rendu. On saute le calque et on continue.
      try {
          if (!calque || calque.type !== 'text' || !calque.regBlock) continue;
          // Un dégradé ou un contour ne se réduisent pas à une couleur : on ne
          // prétend pas trancher.
          if (calque.fillType === 'gradient' || calque.strokeEnabled) continue;
          if (calque.hidden) continue;

          const couleur = styleEffectifTexte(calque).color || '#000000';
          const texte = versRgb(couleur);
          if (!texte) continue;
          const { lo, hi, sur } = fondSous(calque, canvas, elements);
          // La luminance croît avec chaque canal : celle du fond tient entre celles de ses
          // deux bornes, et le meilleur contraste possible est atteint à l'une d'elles.
          const lt = luminance(texte);
          const auMieux = Math.max(rapport(lt, luminance(lo)), rapport(lt, luminance(hi)));
          if (auMieux >= seuil) continue;

          out.push({
              role: calque.regBlock.role,
              key: calque.regBlock.role === 'pictogram'
                  ? `pictogram::${calque.regBlock.pictogramCode || ''}`
                  : calque.regBlock.role,
              name: calque.name,
              couleur,
              contraste: Math.round(auMieux * 100) / 100,
              fond: hex(lo) === hex(hi) ? hex(lo) : `${hex(lo)}–${hex(hi)}`,
              sur: sur ? sur.id : null,
              ...(sur ? { sur_nom: sur.name || sur.id } : {}),
          });
      } catch (_) { /* calque illisible par ce contrôle : on ne conclut rien */ }
    }
    return out;
}
